import { FunctionComponent, ReactElement, useEffect } from 'react';

import 'chime-ui/normalize.css';
import 'chime-ui/custom.css';
// ChimeKit 4.0 theme (compiled by Tailwind v4 via postcss). Scoped to the RFA guest flow
// through `data-theme="chimekit4"` on RfaScreen, so non-RFA pages are unaffected.
// Must stay after normalize.css: its form-control reset wins on source order.
import 'chime-ui/chimekit4.css';
import 'custom-event-polyfill';
import 'proxy-polyfill';

import { AnalyticsProvider } from '@1debit/analytics';
import { DeviceIdProvider } from '@1debit/device-intelligence';
import { datadogRum } from '@datadog/browser-rum';
import { LazyMotion } from 'framer-motion';
import { AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import smoothscroll from 'smoothscroll-polyfill';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

import { colors, fonts, typography } from 'chime-ui/system';
import media from 'chime-ui/system/media';

import { UserContextProvider } from 'hooks/useUserContext';

import { getAnalyticsHandler } from 'utils/analytics/eventRouter';
import { redactRumEvent } from 'utils/datadog/redactRumEvent';
import { deviceIntelligenceEnvironment } from 'utils/device-intelligence/deviceIntelligenceUtils';
import { initExPlat } from 'utils/ExPlat/explat';
import { ApolloProviderWrapper } from 'utils/graphql/client';

if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
  require('../mocks');
}

const {
  publicRuntimeConfig: { deployEnvironment },
} = getConfig();

const isProductionBuild = process.env.NODE_ENV === 'production';
const isDevelopmentBuild = process.env.NODE_ENV === 'development';
const assetBaseUrl = isDevelopmentBuild ? '' : '/pay';

if (isProductionBuild) {
  datadogRum.init({
    applicationId: process.env.DATADOG_APPLICATION_ID,
    clientToken: process.env.DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'pay-anyone-client',
    env: deployEnvironment,
    version: process.env.CIRCLE_SHA1,
    sampleRate: 100,
    premiumSampleRate: 0,
    trackInteractions: true,
    allowedTracingOrigins: [`${process.env.CONSUMER_HOST}`],
    beforeSend: redactRumEvent,
  });
}

const loadFramerMotionFeatures = () =>
  import('utils/framer-motion/features').then(res => res.default);

export default function PayAnyoneClient({
  Component,
  pageProps,
}: AppProps): ReactElement {
  // @1debit/icons bundles a copy of @types/react 18 with its own global JSX, whose
  // ReactNode lacks the bigint/Promise that Next 13+'s react/canary types add, so
  // class-component types no longer pass as JSX. Drop once icons stops bundling it.
  const Page = Component as FunctionComponent<typeof pageProps>;
  initExPlat();
  const router = useRouter();

  const theme = {
    colors,
    fonts,
    typography,
  };

  useEffect(() => {
    // Polyfill for smooth scrolling in Safari & iOS
    // Placed here because it depends on window
    smoothscroll.polyfill();
  }, []);

  return (
    <DeviceIdProvider
      environment={deviceIntelligenceEnvironment(deployEnvironment)}
    >
      <AnalyticsProvider
        handler={getAnalyticsHandler()}
        pageName={router.pathname}
        env={deployEnvironment}
      >
        <ApolloProviderWrapper>
          {/* Site-wide link-preview defaults. They live here, not in _document,
              and are keyed by property so a page can replace them with its own
              (see RfaShareMeta). */}
          <Head>
            <meta
              property="og:title"
              content="Chime - Fee-free instant cashout"
              key="og:title"
            />
            <meta
              property="og:image"
              content="https://member.chime.com/pay/icons/sms-link-preview.png"
              key="og:image"
            />
          </Head>
          <ThemeProvider theme={theme}>
            <LazyMotion features={loadFramerMotionFeatures} strict>
              <GlobalStyle />
              <UserContextProvider>
                <Page {...pageProps} />
              </UserContextProvider>
            </LazyMotion>
          </ThemeProvider>
        </ApolloProviderWrapper>
      </AnalyticsProvider>
    </DeviceIdProvider>
  );
}

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Circular Pro';
    font-style: normal;
    font-weight: 500;
    font-display: fallback;
    src: url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-book.eot'
    }); /* IE9 Compat Modes */
    src: local('Circular Pro Book'), local('CircularPro-Book'),
      url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.eot?#iefix'
      }) format('embedded-opentype'),
      /* IE6-IE8 */ url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.woff2'
      }) format('woff2'),
      /* Super Modern Browsers */ url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.woff'
      })
        format('woff'); /* Modern Browsers */
  }

  @font-face {
    font-family: 'Circular Pro';
    font-style: normal;
    font-weight: 700;
    font-display: fallback;
    src: url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.eot'
    }); /* IE9 Compat Modes */
    src: local('Circular Pro Bold'), local('CircularPro-Bold'),
    url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.eot?#iefix'
    }) format('embedded-opentype'),
    /* IE6-IE8 */ url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.woff2'
    }) format('woff2'),
    /* Super Modern Browsers */ url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.woff'
    })
      format('woff'); /* Modern Browsers */
  }

  body{
    font-family: ${fonts.primary};
    color: ${colors.notBlack};
    background-color: ${colors.white};
    min-height: 100%;
    height: auto;

    ${media.tablet`
      background-color: ${colors.alabaster};
    `};
  }`;
